import React, { useEffect, useMemo } from "react"
import { Tag, Flex, Card, Spin, Collapse, Table, Typography } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { getCrewBookings } from "store/crew-profiles"
import styles from "./index.module.css"
import PropTypes from "prop-types"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import { BookingNotFound } from "ui/Icon/DSIcon/BookingNotFound"
import { Text } from "ui/Typography"
import { useTranslation } from "react-i18next"
import { colors } from "themes/variables"
import moment from "moment"
import ExternalLink from "ui/Icon/DSIcon/ExternalLink"
import { useDevice } from "hooks/useDevice"
import { generateBookingLink } from "pages/guide/resources/equipment/modal/DeactivateDelete/utils"
import Separator from "ui/Separator"

const { Title } = Typography

const UpcomingBookings = ({ crewMemberId }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { isMobile } = useDevice()

  // Get bookings data from Redux store
  const { data: bookingsData = [], loading } = useSelector((state) => state.crewProfiles.bookings)

  // Fetch bookings on mount
  useEffect(() => {
    if (crewMemberId) {
      dispatch(
        getCrewBookings(crewMemberId, {
          status: "confirmed",
          start_date: moment().format("YYYY-MM-DD"),
          sort_by: "booked_date",
          sort_order: "asc"
        })
      )
    }
  }, [dispatch, crewMemberId])

  // Transform API data to component format (group by date)
  const groupedBookings = useMemo(() => {
    if (!bookingsData.length) return []

    const groups = bookingsData.reduce((acc, booking) => {
      const date = moment(booking.booked_date).format("dddd, MMMM D, YYYY")
      
      if (!acc[date]) {
        acc[date] = []
      }

      acc[date].push({
        id: booking.id,
        experienceName: booking.trip?.title || "N/A",
        time: `${booking.start_time} - ${booking.end_time}`,
        clientName: booking.client?.name || "N/A",
        crewMembers: booking.crew_count || 0,
        equipment: booking.equipment_count || 0,
        paymentStatus: booking.payment_status === "full_paid" ? "Paid" : booking.payment_status === "partial" ? "Partial Paid" : "Unpaid",
        bookingLink: generateBookingLink(booking)
      })

      return acc
    }, {})

    return Object.entries(groups).map(([date, bookings]) => ({
      date,
      bookings
    }))
  }, [bookingsData])

  const totalCount = bookingsData.length

  // Table columns for bookings
  const bookingColumns = [
    {
      title: t("crewMember.bookings.columns.experienceName", { defaultValue: "Experience Name" }),
      dataIndex: "experienceName",
      key: "experienceName",
      render: (text, record) => (
        <a href={record.bookingLink} target="_blank" rel="noopener noreferrer" className={styles.experienceLink}>
          {text} <ExternalLink />
        </a>
      )
    },
    {
      title: t("crewMember.bookings.columns.time", { defaultValue: "Time" }),
      dataIndex: "time",
      key: "time"
    },
    {
      title: t("crewMember.bookings.columns.id", { defaultValue: "ID" }),
      dataIndex: "id",
      key: "id"
    },
    {
      title: t("crewMember.bookings.columns.clientName", { defaultValue: "Client Name" }),
      dataIndex: "clientName",
      key: "clientName"
    },
    {
      title: t("crewMember.bookings.columns.crewMembers", { defaultValue: "Crew Member(s)" }),
      dataIndex: "crewMembers",
      key: "crewMembers"
    },
    {
      title: t("crewMember.bookings.columns.equipment", { defaultValue: "Equipment" }),
      dataIndex: "equipment",
      key: "equipment"
    },
    {
      title: t("crewMember.bookings.columns.paymentStatus", { defaultValue: "Payment Status" }),
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      render: (status) => (
        <Tag className={status === "Paid" ? styles.paidTag : styles.outstandingTag}>{status}</Tag>
      )
    }
  ]

  if (loading && !bookingsData.length) {
    return (
      <Card className={styles.bookingsCard}>
        <Flex justify="space-between" align="center" className={styles.sectionHeader}>
          <Title level={4}>
            {t("crewMember.bookings.title", { defaultValue: "Upcoming Bookings" })} <span className={styles.totalCount}>(0)</span>
          </Title>
        </Flex>
        <Flex justify="center" align="center" style={{ padding: "60px 0" }}>
          <Spin size="large" />
        </Flex>
      </Card>
    )
  }

  return (
    <Card className={styles.bookingsCard}>
      <Flex justify="space-between" align="center" className={styles.sectionHeader}>
        <Title level={4}>
          {t("crewMember.bookings.title", { defaultValue: "Upcoming Bookings" })} <span className={styles.totalCount}>({totalCount})</span>
        </Title>
      </Flex>

      <div className={styles.scrollContainer}>
        <WithEmptyStateWrapper
          name="booking"
          hasItems={groupedBookings.length > 0}
          fallback={
            <Flex vertical justify="center" align="center" gap={12} style={{ padding: "60px 0" }}>
              <BookingNotFound />
              <Text color={colors.textPrimary} weight="semibold">
                {t("crewMember.bookings.emptyState.title", { defaultValue: "No upcoming bookings" })}
              </Text>
              <Text color={colors.textSecondaryDark}>
                {t("crewMember.bookings.emptyState.subtitle", { defaultValue: "Bookings will appear here once scheduled" })}
              </Text>
            </Flex>
          }
        >
          <Collapse
            defaultActiveKey={[groupedBookings[0]?.date]}
            expandIconPosition="end"
            className={styles.bookingsCollapse}
            items={groupedBookings.map((dateGroup) => ({
              key: dateGroup.date,
              label: <Text strong>{dateGroup.date}</Text>,
              children: isMobile ? (
                <Flex vertical className={styles.mobileBookingsList}>
                  {dateGroup.bookings.map((booking) => (
                    <Flex key={booking.id} vertical className={styles.mobileBookingCard}>
                      <a href={booking.bookingLink} target="_blank" rel="noopener noreferrer" className={styles.mobileExperienceLink}>
                        {booking.experienceName} <ExternalLink />
                      </a>

                      <Flex align="center" gap={8} wrap className={styles.mobileBookingTags}>
                        <Tag className={booking.paymentStatus === "Paid" ? styles.paidTag : styles.outstandingTag}>{booking.paymentStatus}</Tag>
                        <Tag className={styles.idTag}>ID: {booking.id}</Tag>
                      </Flex>

                      <Flex align="center" gap={8} wrap className={styles.mobileBookingMetaRow}>
                        <Text className={styles.mobileBookingMetaText}>{booking.time}</Text>
                        <Separator />
                        <Text className={styles.mobileBookingMetaText}>{booking.clientName}</Text>
                      </Flex>

                      <Flex align="center" gap={8} wrap className={styles.mobileBookingMetaRow}>
                        <Flex align="center" gap={4} className={styles.mobileBookingStat}>
                          <Text className={styles.mobileBookingLabel}>
                            {t("crewMember.bookings.columns.crewMembers", { defaultValue: "Crew Member(s)" })}:
                          </Text>
                          <Text className={styles.mobileBookingValue}>{booking.crewMembers}</Text>
                        </Flex>
                        <Separator />
                        <Flex align="center" gap={4} className={styles.mobileBookingStat}>
                          <Text className={styles.mobileBookingLabel}>
                            {t("crewMember.bookings.columns.equipment", { defaultValue: "Equipment" })}:
                          </Text>
                          <Text className={styles.mobileBookingValue}>{booking.equipment}</Text>
                        </Flex>
                      </Flex>
                    </Flex>
                  ))}
                </Flex>
              ) : (
                <Table
                  dataSource={dateGroup.bookings}
                  columns={bookingColumns}
                  pagination={false}
                  rowKey="id"
                  className={styles.bookingsTable}
                  scroll={{ x: 800 }}
                />
              )
            }))}
          />
        </WithEmptyStateWrapper>
      </div>
    </Card>
  )
}

UpcomingBookings.propTypes = {
  crewMemberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
}

export default UpcomingBookings
